import React, { useState } from 'react';
import { Wrench, Brain, Database, Code, Zap, Globe, ChevronDown, ChevronRight, GitBranch, Shield, Mail,Layers } from 'lucide-react';
import PageHeader from '../components/PageHeader';

const Documentation = () => {
  const [activeSection, setActiveSection] = useState(null);
  
  const currentOrigin = window.location.origin;
  const backendUrl = process.env.REACT_APP_API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:8090`;
  const cleanBackendUrl = backendUrl.endsWith('/api') ? backendUrl.replace('/api', '') : backendUrl;

  const toggleSection = (sectionId) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  const sections = [
    {
      id: 'instructions',
      title: 'Agent Instructions',
      icon: <Brain size={18} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600 text-sm leading-relaxed">
            Base instructions guide the persona, tone, and strict rules for the AI Support Team. The AI will prioritize these rules over all other behavioral tendencies.
          </p>
          <div className="bg-blue-50/50 rounded-2xl p-5 border border-blue-100/50 text-[11px] text-blue-900">
            <strong>Pro Tip:</strong> Use the "Optimize Instruction" button to let the AI rewrite your raw ideas into a professional, highly structured, prompt-engineered persona.
          </div>
        </div>
      )
    },
    {
      id: 'analytics',
      title: 'Analytics & Insights',
      icon: <Database size={18} />,
      content: (
        <>
          <p className="text-gray-600 mb-6 text-sm leading-relaxed">
            Monitor the performance and cost of your AI infrastructure in real-time with high-precision metrics.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <h4 className="text-[10px] font-black text-blue-600 mb-1 uppercase">Tokenomics</h4>
              <p className="text-[10px] text-gray-500 leading-relaxed">Track input/output tokens to optimize LLM spending.</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <h4 className="text-[10px] font-black text-purple-600 mb-1 uppercase">Sentiment</h4>
              <p className="text-[10px] text-gray-500 leading-relaxed">Analyze user emotions to refine agent empathy protocols.</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <h4 className="text-[10px] font-black text-green-600 mb-1 uppercase">Sessions</h4>
              <p className="text-[10px] text-gray-500 leading-relaxed">Review full transcriptions and behavioral flow patterns.</p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'tools',
      title: 'API Tools & Neural Extensions (MCP)',
      icon: <Wrench size={18} />,
      content: (
        <div className="space-y-8">
          <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Custom API Tools</h3>
            <p className="text-xs text-gray-600 mb-4 leading-relaxed">
              Standard API Tools allow your agent to perform real-world actions like fetching data or triggering webhooks using manual endpoint mapping.
            </p>
            <ul className="list-disc list-inside text-[11px] text-gray-500 space-y-2 ml-1">
              <li><strong className="text-gray-700">Method & URL:</strong> Define the HTTP endpoint for your external service.</li>
              <li><strong className="text-gray-700">Parameter Schema:</strong> Use "LLM Prompt" to extract data from chat or "Constant" for static values.</li>
            </ul>
          </div>
          <div className="bg-blue-50/50 rounded-2xl p-6 border border-blue-100/30">
            <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
              <Code size={16} /> Model Context Protocol (MCP)
            </h3>
            <p className="text-[11px] text-blue-800/80 leading-relaxed">
              Enable <strong>MCP Mode</strong> in the API Tools dashboard to connect to technical bridge servers. 
              MCP allows the system to automatically discover and use technical tools without manual configuration.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'webhooks',
      title: 'API Webhooks',
      icon: <Code size={18} />,
      content: (
        <div className="space-y-6">
          <p className="text-xs text-gray-600 leading-relaxed">
            Integrate your AI assistant with your own custom backends or applications using the Webhook URL and API Key.
          </p>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
            <h4 className="text-[11px] font-bold text-blue-900 mb-2 flex items-center gap-2">
              <Database size={12} /> Available Request Fields
            </h4>
            <ul className="space-y-1.5 text-[10px] text-blue-800">
              <li><code className="bg-blue-100 px-1 rounded font-bold">message</code> (Required): The text query for the AI.</li>
              <li><code className="bg-blue-100 px-1 rounded font-bold">user_id</code> (Optional): Unique identifier for your end-user.</li>
              <li><code className="bg-blue-100 px-1 rounded font-bold">session_id</code> (Optional): A UUID to continue a chat session.</li>
              <li><code className="bg-blue-100 px-1 rounded font-bold">metadata</code> (Optional): JSON object for additional context.</li>
            </ul>
          </div>
          <div className="bg-gray-900 rounded-xl p-5 border border-gray-800 shadow-xl overflow-hidden relative">
            <div className="absolute top-0 right-0 p-2 opacity-50"><Code size={30} className="text-blue-500" /></div>
            <pre className="text-[10px] font-mono text-blue-300 overflow-x-auto">
{`# Usage Example:
curl -X POST "${cleanBackendUrl}/webhook/incoming" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "message": "Hello AI!",
    "user_id": "customer_123",
    "metadata": { "source": "mobile_app" }
  }'`}
            </pre>
          </div>
        </div>
      )
    },
    {
      id: 'campaigns',
      title: 'Campaign Orchestration',
      icon: <Zap size={18} />,
      content: (
        <>
          <p className="text-gray-600 mb-6 text-sm leading-relaxed">
            Boost engagement using behavioral triggers. Campaigns allow you to push proactive messages or offers to visitors based on their journey.
          </p>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
              <h4 className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wide">Templates & Conditions</h4>
              <ul className="list-disc list-inside text-[11px] text-gray-500 space-y-1 ml-1">
                <li><strong className="text-gray-700">Forge New:</strong> Start from scratch or use pre-built neural templates.</li>
                <li><strong className="text-gray-700">Triggers:</strong> Set logic for when a campaign appears (e.g., exit intent, time on page).</li>
                <li><strong className="text-gray-700">Priority:</strong> Orchestrate which campaign takes precedence using Priority Levels (P0-P10).</li>
              </ul>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'integrations',
      title: 'Channel Integrations',
      icon: <Zap size={18} />,
      content: (
        <div className="space-y-8">
          {/* Slack */}
          <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-5 h-5 rounded bg-gray-900 text-white flex items-center justify-center text-[10px] font-bold">#</span>
              <h3 className="text-base font-bold text-gray-900">Slack Bot Setup</h3>
            </div>
            <div className="space-y-4 text-xs text-gray-600 leading-relaxed">
              <p>Connect your agent to a Slack workspace by following these steps:</p>
              <ol className="list-decimal list-inside space-y-3 ml-1">
                <li>Create a <strong>Slack App</strong> at <a href="https://api.slack.com/apps" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">api.slack.com/apps</a>.</li>
                <li>Add scopes: <code className="bg-gray-100 px-1 rounded">chat:write</code>, <code className="bg-gray-100 px-1 rounded">app_mentions:read</code>, <code className="bg-gray-100 px-1 rounded">im:history</code>.</li>
              </ol>
            </div>
          </div>
          {/* Telegram */}
          <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
            <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
               Telegram Messenger
            </h3>
            <p className="text-xs text-gray-600 mb-4 leading-relaxed">
              Talk to <strong>@BotFather</strong> to get a <strong>Bot Token</strong>, then paste it in the Telegram Integration page.
            </p>
          </div>
          {/* Freshchat */}
          <div className="bg-blue-50/30 rounded-2xl p-6 border border-blue-100/50">
            <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
               Freshchat Messenger
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed mb-4">
              Integrate your agent with Freshchat to handle human handoffs and multi-channel support.
            </p>
            <ul className="list-disc list-inside text-[11px] text-gray-500 space-y-2 ml-1">
              <li>Enter your <strong>App ID</strong> and <strong>App Key</strong> from the Freshchat dashboard.</li>
              <li>Configure the <strong>Webview URL</strong> to allow the agent to surface in the Freshchat inbox.</li>
            </ul>
          </div>
          {/* Email */}
          <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
            <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
               Email Outbound Protocol
            </h3>
            <p className="text-xs text-gray-600 mb-4 leading-relaxed">
              Configure your agent to send automated email notifications or responses using your own SMTP server.
            </p>
            <ul className="list-disc list-inside text-[11px] text-gray-500 space-y-3 ml-1 leading-relaxed">
              <li><strong className="text-gray-700">SMTP Server:</strong> e.g., <code>smtp.gmail.com</code> for Google or your corporate mail server.</li>
              <li><strong className="text-gray-700">App Passkey:</strong> For Gmail, generate a 16-character <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">App Password</a>.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'knowledge',
      title: 'Data Sources & Knowledge',
      icon: <Database size={18} />,
      content: (
        <div className="space-y-10">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2">Document Training</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                Upload PDF, DOCX, or CSV files. The AI chunks and stores these locally using <strong>PgVector</strong> vector embeddings. When a user asks a question, the Agent performs a semantic search to find relevant context.
              </p>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 text-[10px] text-gray-500">
                Ideal for: Manuals, Policy Docs, Broad Knowledge.
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2">Smart FAQ Builder</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                Define explicit Question-Answer pairs. Unlike documents which the AI may summarize, Smart FAQs force the AI to return exact responses when specific triggers are met.
              </p>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 text-[10px] text-gray-500">
                Ideal for: Pricing, Exact Contact Info, Fixed Procedures.
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Layers size={16} className="text-blue-600" /> Vector Configuration & Strategies
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="text-[11px] font-bold text-gray-700 uppercase mb-1">Chunking Strategies</h4>
                  <ul className="text-[10px] text-gray-500 space-y-2">
                    <li><strong className="text-gray-900">Recursive Splitting:</strong> Default method; splits by natural boundaries like periods.</li>
                    <li><strong className="text-gray-900">Semantic AI:</strong> Groups sentences by meaning for maximum context preservation.</li>
                    <li><strong className="text-gray-900">Markdown Aware:</strong> Respects headers (#) for structured technical docs.</li>
                  </ul>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="text-[11px] font-bold text-gray-700 uppercase mb-1">Neural Parameters</h4>
                  <ul className="text-[10px] text-gray-500 space-y-2">
                    <li><strong className="text-gray-900">Chunk Size:</strong> Number of characters per slice (Default: 2000).</li>
                    <li><strong className="text-gray-900">Overlap:</strong> Shared text between chunks to maintain continuity.</li>
                    <li><strong className="text-gray-900">Top-K:</strong> Number of relevant chunks retrieved per query (Default: 5).</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200/50">
              <p className="text-[10px] text-amber-600 font-bold italic bg-amber-50 p-3 rounded-xl border border-amber-100">
                Note: Parameters are set at the time of upload. Modifying settings will only affect future document ingestion.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'domains',
      title: 'Domain Authorization',
      icon: <Globe size={18} />,
      content: (
        <>
          <p className="text-gray-600 mb-6 text-sm leading-relaxed">
            For security reasons, your chat widget will only load on domains that you have explicitly authorized. This prevents unauthorized websites from using your AI agent.
          </p>
          <div className="bg-amber-50 rounded-2xl p-6 border border-amber-100">
            <h3 className="text-sm font-bold text-amber-900 mb-3">How to Authorize a Domain</h3>
            <ol className="list-decimal list-inside text-xs text-amber-800 space-y-3 ml-1">
              <li>Navigate to the <strong className="text-amber-950">Domain Management</strong> tab in the sidebar.</li>
              <li>Enter your website domain (e.g., <code className="bg-white/50 px-1 rounded">example.com</code> or <code className="bg-white/50 px-1 rounded">localhost:3000</code>).</li>
              <li>Click <strong className="text-amber-950">Add Domain</strong> to whitelist it. The widget will now load on that site.</li>
            </ol>
          </div>
        </>
      )
    },
    {
      id: 'flows',
      title: 'Neural Business Flows',
      icon: <GitBranch size={18} />,
      content: (
        <>
          <p className="text-gray-600 mb-6 text-sm leading-relaxed">
            Define structured processes that the AI must follow. Ideal for complex workflows like order lookups or verification steps.
          </p>
          <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
            <h4 className="text-xs font-bold text-amber-900 mb-2">The Activation Protocol</h4>
            <p className="text-[11px] text-amber-800 leading-relaxed">
              Use the <code className="bg-white/50 px-1 rounded font-bold">business_flow_loader</code> tool in your Agent Instructions to connect a specific flow name to the AI's operational logic.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'widget',
      title: 'Widget Interface',
      icon: <Code size={18} />,
      content: (
        <div className="space-y-10">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <h3 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2">Core Integration</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                Connect your AI agent to any website with a single script tag. Authorize your domain in the <strong>Domain Management</strong> tab before deployment.
              </p>
              <div className="relative">
                <pre className="text-[10px] font-mono bg-gray-900 text-blue-300 p-4 rounded-xl overflow-x-auto border border-blue-900/30">
{`<script src="${currentOrigin}/widget.js"></script>
<script>
  ChatWidget.init({
    agentKey: 'YOUR_KEY',
    apiUrl: '${cleanBackendUrl}'
  });
</script>`}
                </pre>
              </div>
            </div>
            <div className="space-y-6">
              <h3 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2">Bespoke Visual Identity</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                Configure your touchpoint's aesthetics to match your brand's digital identity.
              </p>
              <ul className="text-[10px] text-gray-500 space-y-2">
                <li><strong className="text-gray-900">Adaptive Sizing:</strong> Small (300px), Medium (350px), or Large (400px) viewports.</li>
                <li><strong className="text-gray-900">Typography:</strong> Select from 10+ premium fonts and set precise font sizes.</li>
                <li><strong className="text-gray-900">Custom Branding:</strong> Upload 1:1 Bot Icons and Header Logos for a white-labeled experience.</li>
              </ul>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Zap size={16} className="text-blue-500" />
              <h3 className="text-sm font-bold text-gray-900">Advanced Neural Engagement</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <h4 className="text-[11px] font-bold text-blue-600 uppercase">AI Learning</h4>
                <p className="text-[10px] text-gray-500 leading-relaxed">Background memory extraction to build a persistent agent of user preferences and history.</p>
              </div>
              <div className="space-y-2">
                <h4 className="text-[11px] font-bold text-blue-600 uppercase">Feedback Loop</h4>
                <p className="text-[10px] text-gray-500 leading-relaxed">Gather user sentiment via star ratings and comments on every response to optimize the brain.</p>
              </div>
              <div className="space-y-2">
                <h4 className="text-[11px] font-bold text-blue-600 uppercase">Sequential Queue</h4>
                <p className="text-[10px] text-gray-500 leading-relaxed">If multiple marketing campaigns match user intent, show them in a prioritized, sequential flow.</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4 text-gray-900">
              <Database size={16} className="text-blue-500" />
              <h3 className="text-sm font-bold">Memory & Context Optimization</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <h4 className="text-[11px] font-bold text-blue-800 uppercase">Summarization Protocol</h4>
                <p className="text-[10px] text-gray-600 leading-relaxed">
                  Long conversations are automatically distilled using high-intelligence models (GPT-4o/Claude) to keep context windows clean and costs low.
                </p>
                <div className="bg-gray-50 p-2 rounded-lg border border-gray-100 text-[9px] text-blue-600 font-bold uppercase inline-block">
                  Adjustable Sync Interval (1-20 Messages)
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="text-[11px] font-bold text-blue-800 uppercase">Neural Context Injection</h4>
                <p className="text-[10px] text-gray-600 leading-relaxed">
                  Seamlessly pass data from your existing user sessions (Name, Email, Account ID) into the agent's memory for hyper-personalized assistance.
                </p>
                
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-16">
        <PageHeader 
          title="Protocol Manuals"
          description="Comprehensive blueprints for mastering the EasyChat neural architecture."
        />

        <div className="space-y-4 animate-slideIn">
          {sections.map((section) => (
            <div key={section.id} className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-100/50 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-gray-200/50">
              <button 
                onClick={() => toggleSection(section.id)}
                className="w-full p-5 flex items-center justify-between hover:bg-gray-50/50 transition-colors group"
              >
                <div className="flex items-center gap-5">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-500 ${activeSection === section.id ? 'bg-gray-900 text-white shadow-lg' : 'bg-gray-50 text-gray-900 group-hover:scale-110'}`}>
                    {section.icon}
                  </div>
                  <h2 className="text-lg font-black text-gray-900 tracking-tight">{section.title}</h2>
                </div>
                {activeSection === section.id ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
              </button>
              
              <div className={`transition-all duration-300 ease-in-out ${activeSection === section.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                <div className="p-6 pt-0 border-t border-gray-50">
                  {section.content}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Documentation;
